/**
 * Modern Monk Coffee — Cart Drawer JS
 * Drop into: assets/mmc-cart-drawer.js
 * Then load in theme.liquid: <script src="{{ 'mmc-cart-drawer.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  // ── State ─────────────────────────────────────────────────────────────────
  let cart = null;
  let recsData = [];
  let recsPage = 0;
  const RECS_PER_PAGE = 2;

  // ── DOM refs ───────────────────────────────────────────────────────────────
  const drawer      = document.getElementById('mmc-cart-drawer');
  const overlay     = document.getElementById('mmc-cart-overlay');
  const closeBtn    = document.getElementById('mmc-close-btn');
  const countEl     = document.getElementById('mmc-count');
  const loadingEl   = document.getElementById('mmc-loading');
  const lineItemsEl = document.getElementById('mmc-line-items');
  const emptyEl     = document.getElementById('mmc-empty');
  const recsEl      = document.getElementById('mmc-recs');
  const recsTrack   = document.getElementById('mmc-recs-track');
  const recsDots    = document.getElementById('mmc-recs-dots');
  const footerEl    = document.getElementById('mmc-drawer-footer');
  const subtotalEl  = document.getElementById('mmc-subtotal');

  // ── Featured products for empty state ─────────────────────────────────────
  // Format: { handle, label }
  const FEATURED_PRODUCTS = [
    { handle: 'nostalgic',      label: 'Bestseller'     },
    { handle: 'abbey-ale-decaf', label: 'Bestseller'    },
    { handle: 'white-wine',     label: 'Limited Edition' },
  ];

  // ── Open / Close ───────────────────────────────────────────────────────────
  function openDrawer() {
    drawer.classList.add('is-open');
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');
    fetchCart();
  }

  function closeDrawer() {
    drawer.classList.remove('is-open');
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
  }

  closeBtn.addEventListener('click', closeDrawer);
  overlay.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
  });

  document.addEventListener('click', (e) => {
    const cartTrigger = e.target.closest(
      'a[href="/cart"], [data-cart-icon-bubble], .header__icon--cart, [aria-label="Cart"]'
    );
    if (cartTrigger) {
      e.preventDefault();
      openDrawer();
    }
  });

  document.addEventListener('cart:open', openDrawer);

  // ── Fetch cart ─────────────────────────────────────────────────────────────
  async function fetchCart() {
    showLoading();
    try {
      const res = await fetch('/cart.js');
      cart = await res.json();
      renderCart();
      fetchRecs();
    } catch (err) {
      console.error('[MMC Cart]', err);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  function renderCart() {
    if (!cart) return;

    const itemCount = cart.item_count || 0;
    countEl.textContent = itemCount;

    document.querySelectorAll('.cart-count-bubble span[aria-hidden]').forEach(el => {
      el.textContent = itemCount;
    });

    const total = cart.total_price || 0;
    subtotalEl.textContent = formatMoney(total);

    hideLoading();

    if (itemCount === 0) {
      lineItemsEl.style.display = 'none';
      footerEl.style.display = 'none';
      renderEmptyState();
      return;
    }

    emptyEl.style.display = 'none';
    footerEl.style.display = 'block';
    lineItemsEl.style.display = 'block';
    lineItemsEl.innerHTML = cart.items.map(renderLineItem).join('');

    lineItemsEl.querySelectorAll('.mmc-qty-btn').forEach(btn => {
      btn.addEventListener('click', handleQtyClick);
    });
    lineItemsEl.querySelectorAll('.mmc-remove-btn').forEach(btn => {
      btn.addEventListener('click', handleRemove);
    });
  }

  // ── Empty State ─────────────────────────────────────────────────────────────
  async function renderEmptyState() {
    emptyEl.style.display = 'flex';

    // Build headline immediately
    emptyEl.innerHTML = `
      <div class="mmc-empty-headline">
        <p class="mmc-empty-eyebrow">Your cup is empty.</p>
        <p class="mmc-empty-title">A good place to start.</p>
      </div>
      <ul class="mmc-empty-products" id="mmc-empty-products">
        <li class="mmc-empty-loading">Loading…</li>
      </ul>
      <div class="mmc-empty-helper">
        <img src="https://cdn.shopify.com/s/files/1/0934/1003/3976/files/Logo64.png?v=1776190283" width="28" height="28" alt="" aria-hidden="true" class="mmc-empty-helper-icon">
        <p class="mmc-empty-helper-text">
          New to Co-Fermented Coffee?<br>
          <a href="https://modernmonkcoffee.com/pages/learn" class="mmc-empty-helper-link">Learn how it's made.</a>
        </p>
      </div>
    `;

    // Fetch each featured product and render
    const listEl = emptyEl.querySelector('#mmc-empty-products');
    const productPromises = FEATURED_PRODUCTS.map(({ handle, label }) =>
      fetch(`/products/${handle}.js`)
        .then(r => r.json())
        .then(p => ({ product: p, label }))
        .catch(() => null)
    );

    const results = await Promise.all(productPromises);
    listEl.innerHTML = results
      .filter(Boolean)
      .map(({ product, label }) => renderEmptyProduct(product, label))
      .join('');

    // Bind add buttons
    listEl.querySelectorAll('.mmc-empty-add-btn').forEach(btn => {
      btn.addEventListener('click', async () => {
        const variantId = parseInt(btn.dataset.variantId, 10);
        btn.disabled = true;
        btn.textContent = '…';
        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: variantId, quantity: 1 }),
          });
          await fetchCart();
        } catch (err) {
          console.error('[MMC Cart] add failed', err);
          btn.disabled = false;
          btn.textContent = '+';
        }
      });
    });
  }

  function renderEmptyProduct(product, label) {
    const variant = product.variants[0];
    const img = product.featured_image || '';
    const price = formatMoney(variant.price);
    return `
      <li class="mmc-empty-product-item">
        <a href="/products/${product.handle}" class="mmc-empty-product-img-link">
          <img src="${img}" alt="${product.title}" width="60" height="60" loading="lazy" class="mmc-empty-product-img">
        </a>
        <div class="mmc-empty-product-info">
          <span class="mmc-empty-product-label">${label}</span>
          <a href="/products/${product.handle}" class="mmc-empty-product-name">${product.title}</a>
          <p class="mmc-empty-product-price">${price}</p>
        </div>
        <button class="mmc-empty-add-btn" data-variant-id="${variant.id}" aria-label="Add ${product.title} to cart">+</button>
      </li>
    `;
  }

  function renderLineItem(item) {
    const imgSrc = item.image ? item.image.replace('_grande', '_medium') : '';
    return `
      <div class="mmc-line-item" data-key="${item.key}">
        <a href="${item.url}">
          <img class="mmc-item-img" src="${imgSrc}" alt="${item.product_title}" loading="lazy">
        </a>
        <div class="mmc-item-info">
          <p class="mmc-item-title">${item.product_title}</p>
          <p class="mmc-item-variant">${item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : ''}</p>
          <div class="mmc-qty-control">
            <button class="mmc-qty-btn" data-key="${item.key}" data-qty="${item.quantity - 1}" aria-label="Decrease">−</button>
            <span class="mmc-qty-value">${item.quantity}</span>
            <button class="mmc-qty-btn" data-key="${item.key}" data-qty="${item.quantity + 1}" aria-label="Increase">+</button>
          </div>
          <button class="mmc-remove-btn" data-key="${item.key}">Remove</button>
        </div>
        <div class="mmc-item-price">${formatMoney(item.line_price)}</div>
      </div>
    `;
  }

  // ── Qty & Remove ───────────────────────────────────────────────────────────
  async function handleQtyClick(e) {
    const btn = e.currentTarget;
    const key = btn.dataset.key;
    const qty = parseInt(btn.dataset.qty, 10);
    if (qty < 0) return;
    await updateLineItem(key, qty);
  }

  async function handleRemove(e) {
    const key = e.currentTarget.dataset.key;
    await updateLineItem(key, 0);
  }

  async function updateLineItem(key, qty) {
    try {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty }),
      });
      cart = await res.json();
      renderCart();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (err) {
      console.error('[MMC Cart] update failed', err);
    }
  }

  // ── Recommendations ────────────────────────────────────────────────────────
  async function fetchRecs() {
    if (!cart || cart.item_count === 0) {
      if (recsEl) recsEl.style.display = 'none';
      return;
    }
    const productId = cart.items[0]?.product_id;
    if (!productId) return;
    try {
      const res = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=6&intent=related`
      );
      const data = await res.json();
      const cartProductIds = cart.items.map(i => i.product_id);
      recsData = (data.products || []).filter(p => !cartProductIds.includes(p.id));
      if (recsData.length === 0) { if (recsEl) recsEl.style.display = 'none'; return; }
      if (recsEl) recsEl.style.display = 'block';
      recsPage = 0;
      renderRecs();
    } catch (err) {
      console.error('[MMC Cart] recs failed', err);
    }
  }

  function renderRecs() {
    if (!recsTrack || !recsDots) return;
    const totalPages = Math.ceil(recsData.length / RECS_PER_PAGE);
    recsTrack.innerHTML = recsData.slice(0, totalPages * RECS_PER_PAGE).map(renderRecCard).join('');
    recsTrack.style.transform = `translateX(calc(-${recsPage * 100 / totalPages}%))`;
    recsTrack.style.width = `${totalPages * 100}%`;
    recsTrack.querySelectorAll('.mmc-rec-add').forEach(btn => {
      btn.addEventListener('click', handleRecAdd);
    });
    recsDots.innerHTML = '';
    if (totalPages > 1) {
      for (let i = 0; i < totalPages; i++) {
        const dot = document.createElement('div');
        dot.className = 'mmc-dot' + (i === recsPage ? ' is-active' : '');
        dot.addEventListener('click', () => { recsPage = i; renderRecs(); });
        recsDots.appendChild(dot);
      }
    }
  }

  function renderRecCard(product) {
    const variant = product.variants[0];
    const price = formatMoney(variant.price);
    return `
      <div class="mmc-rec-card" style="flex: 0 0 calc(${100 / RECS_PER_PAGE}% - 5px)">
        <a href="${product.url}">
          <img class="mmc-rec-img" src="${product.featured_image || ''}" alt="${product.title}" loading="lazy">
        </a>
        <p class="mmc-rec-title">${product.title}</p>
        <p class="mmc-rec-price">${price}</p>
        <button class="mmc-rec-add" data-variant-id="${variant.id}">Add to cart</button>
      </div>
    `;
  }

  async function handleRecAdd(e) {
    const btn = e.currentTarget;
    const variantId = parseInt(btn.dataset.variantId, 10);
    btn.disabled = true;
    btn.textContent = 'Adding…';
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      await fetchCart();
    } catch (err) {
      console.error('[MMC Cart] add failed', err);
      btn.disabled = false;
      btn.textContent = 'Add to cart';
    }
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  function showLoading() {
    loadingEl.style.display = 'flex';
    lineItemsEl.style.display = 'none';
    emptyEl.style.display = 'none';
    if (recsEl) recsEl.style.display = 'none';
    footerEl.style.display = 'none';
  }

  function hideLoading() {
    loadingEl.style.display = 'none';
  }

  document.addEventListener('shopify:section:load', fetchCart);

  const _origFetch = window.fetch;
  window.fetch = function (...args) {
    const promise = _origFetch.apply(this, args);
    const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    if (url.includes('/cart/add')) {
      promise.then(() => {
        setTimeout(fetchCart, 150);
        if (!drawer.classList.contains('is-open')) openDrawer();
      });
    }
    return promise;
  };

  const existingBubble = document.querySelector('.cart-count-bubble span[aria-hidden]');
  if (existingBubble) {
    countEl.textContent = existingBubble.textContent || '0';
  }

})();
